import { inject, computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { OpenApiService } from '../../store/openapi.service';
import { components } from '../../store/schema';
import { AuthService } from './auth.service';

type UserResponse = components['schemas']['UserResponse'];

export type BackofficeAccess =
    | 'admin-only'
    | 'college-staff'
    | 'intake-and-noatzim'
    | 'any-approved'
    | 'unapproved-only'
    | 'pending';

interface AuthStoreState {
    currentUser: UserResponse | null;
    loaded: boolean;
    loading: boolean;
    error: string | null;
}

const initialState: AuthStoreState = {
    currentUser: null,
    loaded: false,
    loading: false,
    error: null,
};

function resolveRole(user: UserResponse | null): string | null {
    if (!user) return null;
    const role = user.role?.name || user.type;
    return role?.trim() || null;
}

function isUnapprovedRole(role: string | null): boolean {
    return role === 'referrer-unapproved' || role === 'pending';
}

function isApprovedRole(role: string | null): boolean {
    return role !== null && !isUnapprovedRole(role);
}

function hasAccess(role: string | null, access: BackofficeAccess): boolean {
    switch (access) {
        case 'admin-only':
            return role === 'college-admin';
        case 'college-staff':
            return role === 'college-admin' || role === 'college-user';
        case 'intake-and-noatzim':
            return role === 'college-admin' || role === 'college-user' || role === 'referrer';
        case 'any-approved':
            return isApprovedRole(role);
        case 'unapproved-only':
        case 'pending':
            return isUnapprovedRole(role);
        default:
            return false;
    }
}

function getLandingRouteForRole(role: string | null): string {
    if (role === 'referrer') return '/backoffice/intake';
    if (role === 'college-admin') return '/backoffice/dashboard';
    if (role === 'college-user') return '/backoffice/dashboard';
    if (isUnapprovedRole(role)) return '/backoffice/pending-approval';
    return '/backoffice/pending-approval';
}

export const AuthStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed((store) => ({
        roleName: computed(() => store.currentUser()?.role?.name ?? null),
        userType: computed(() => resolveRole(store.currentUser())),
        email: computed(() => store.currentUser()?.email ?? null),
        isUnapproved: computed(() => isUnapprovedRole(resolveRole(store.currentUser()))),
        isApproved: computed(() => isApprovedRole(resolveRole(store.currentUser()))),
        canViewRoles: computed(() => hasAccess(resolveRole(store.currentUser()), 'admin-only')),
        canViewDashboard: computed(() => hasAccess(resolveRole(store.currentUser()), 'college-staff')),
        canViewUsers: computed(() => hasAccess(resolveRole(store.currentUser()), 'college-staff')),
        canViewForms: computed(() => hasAccess(resolveRole(store.currentUser()), 'college-staff')),
        canViewBlocks: computed(() => hasAccess(resolveRole(store.currentUser()), 'college-staff')),
        canEditUsers: computed(() => hasAccess(resolveRole(store.currentUser()), 'admin-only')),
        canEditForms: computed(() => hasAccess(resolveRole(store.currentUser()), 'admin-only')),
        canEditBlocks: computed(() => hasAccess(resolveRole(store.currentUser()), 'admin-only')),
        canViewIntake: computed(() => hasAccess(resolveRole(store.currentUser()), 'intake-and-noatzim')),
        canViewNoatzim: computed(() => hasAccess(resolveRole(store.currentUser()), 'intake-and-noatzim')),
        landingRoute: computed(() => getLandingRouteForRole(resolveRole(store.currentUser()))),
    })),
    withMethods((store, openApi = inject(OpenApiService), auth = inject(AuthService)) => {
        let inFlightLoad: Promise<UserResponse | null> | null = null;

        const loadCurrentUser = async (force = false): Promise<UserResponse | null> => {
            if (!force && store.loaded()) {
                return store.currentUser();
            }
            if (inFlightLoad) {
                return inFlightLoad;
            }

            patchState(store, { loading: true, error: null });
            inFlightLoad = firstValueFrom(openApi.getAuthenticatedUser())
                .then((user) => {
                    patchState(store, {
                        currentUser: user,
                        loaded: true,
                        loading: false,
                        error: null,
                    });
                    return user;
                })
                .catch((error: { message?: string }) => {
                    patchState(store, {
                        currentUser: null,
                        loaded: true,
                        loading: false,
                        error: error.message ?? 'Error',
                    });
                    throw error;
                })
                .finally(() => {
                    inFlightLoad = null;
                });

            return inFlightLoad;
        };

        const clear = (): void => {
            patchState(store, { ...initialState });
        };

        const logout = async (): Promise<void> => {
            try {
                await auth.signOut();
            } finally {
                clear();
            }
        };

        const canAccess = (access: BackofficeAccess): boolean => {
            return hasAccess(resolveRole(store.currentUser()), access);
        };

        return {
            loadCurrentUser,
            clear,
            logout,
            canAccess,
        };
    }),
);
